import { CheckCircleIcon, ChevronDoubleLeftIcon, DocumentTextIcon } from "@heroicons/react/24/outline";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";

import {
  IInboxConversation,
  InboxFolder,
} from "../../models/messagesInbox/IInboxConversation";
import classNames from "../../utils/classNames";
import { receivedLabel } from "../../utils/inboxDates";
import { isOverdue, InboxSort, lastSentAt } from "../../utils/inboxFolders";
import { replyByLabel } from "../../utils/replyByLabel";
import {
  ChannelBadge,
  Chip,
  ReplyByChip,
  stateChipFor,
} from "./InboxBits";

interface ConversationListProps {
  conversations: IInboxConversation[];
  folder: InboxFolder;
  selectedId?: string;
  onSelect: (id: string) => void;
  sort: InboxSort;
  onSort: (sort: InboxSort) => void;
  now: DateTime;
  /** Shown above the list when a channel filter is hiding buyers who are waiting. */
  nudge?: { count: number; oldest: IInboxConversation; onShow: () => void };
  nextReplyDue?: string;
  waitingOnBuyerCount: number;
  onFolder: (folder: InboxFolder) => void;
  /** Hides the list to give the conversation and the side panel more room. */
  onHide: () => void;
  /** Live data: a cancel or refund is in flight, so the open conversation stays put until it answers. */
  disabled?: boolean;
}

const timeReceived = receivedLabel;

export default function ConversationList({
  conversations,
  folder,
  selectedId,
  onSelect,
  sort,
  onSort,
  now,
  nudge,
  nextReplyDue,
  waitingOnBuyerCount,
  onFolder,
  onHide,
  disabled = false,
}: ConversationListProps) {
  const { t } = useTranslation("messages");
  return (
    <div className="flex w-[300px] shrink-0 flex-col border-r border-gray-200 bg-white">
      <div className="flex h-10 shrink-0 items-center gap-2 border-b border-gray-200 px-3">
        {folder === "sent" ? (
          <span className="text-[13px] text-gray-700">
            {t("Most recently sent")}
          </span>
        ) : (
          <select
            value={sort}
            onChange={(e) => onSort(e.target.value as InboxSort)}
            className="border-0 bg-transparent py-0 pl-0 pr-7 text-[13px] text-gray-700 focus:ring-0"
          >
            <option value="least_time_left">{t("Least time left")}</option>
            <option value="oldest_first">{t("Oldest first")}</option>
          </select>
        )}
        <span className="ml-auto text-[12px] text-gray-400">
          {conversations.length}
        </span>
        <button
          type="button"
          onClick={onHide}
          title={t("Hide the list")}
          className="rounded p-1 text-gray-500 hover:bg-gray-100"
        >
          <ChevronDoubleLeftIcon className="h-4 w-4" />
        </button>
      </div>

      {nudge && (
        <div className="m-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-[13px]">
          <p className="font-semibold text-amber-800">
            {nudge.count === 1
              ? t("{{count}} PartPro buyer is waiting", { count: nudge.count })
              : t("{{count}} PartPro buyers are waiting", { count: nudge.count })}
          </p>
          <p className="mt-0.5 text-amber-800">
            {t("Oldest is {{name}} about {{part}}.", {
              name: nudge.oldest.buyer.username,
              part: nudge.oldest.part?.shortTitle ?? t("a part"),
            })}
          </p>
          <button
            type="button"
            onClick={nudge.onShow}
            className="mt-2 rounded-md bg-amber-600 px-2.5 py-1 font-medium text-white hover:bg-amber-700"
          >
            {t("Show PartPro")}
          </button>
        </div>
      )}

      <ul className="min-h-0 flex-1 overflow-y-auto">
        {conversations.length === 0 && (
          <li className="flex flex-col items-center px-6 py-12 text-center">
            {folder === "needs_reply" ? (
              <>
                <CheckCircleIcon className="h-10 w-10 text-green-600" />
                <p className="mt-2 text-[14px] font-semibold text-gray-900">
                  {t("Nothing needs a reply")}
                </p>
                {nextReplyDue && (
                  <p className="mt-1 text-[13px] text-gray-500">
                    {t("Next reply due in {{time}}.", { time: nextReplyDue })}
                  </p>
                )}
                <div className="mt-3 flex gap-2">
                  <button
                    type="button"
                    onClick={() => onFolder("sent")}
                    className="rounded-md border border-gray-200 px-2.5 py-1 text-[12px] text-gray-700 hover:bg-gray-50"
                  >
                    {t("Sent · {{count}}", { count: waitingOnBuyerCount })}
                  </button>
                </div>
              </>
            ) : (
              <p className="text-[13px] text-gray-500">
                {t("No conversations here.")}
              </p>
            )}
          </li>
        )}

        {conversations.map((c) => {
          const selected = c.id === selectedId;
          const overdue = isOverdue(c, now);
          const chip = stateChipFor(c);
          const sentAt = folder === "sent" ? lastSentAt(c) : undefined;
          const countdown = sentAt
            ? {
                text: t("Sent {{time}}", { time: timeReceived(sentAt, now) }),
                tone: "neutral" as const,
              }
            : c.status === "needs_reply"
              ? replyByLabel(c.replyBy, c.lastBuyerMessageAt, now)
              : {
                  text: timeReceived(c.lastBuyerMessageAt, now),
                  tone: "neutral" as const,
                };

          return (
            <li
              key={c.id}
              className={classNames(
                "relative flex gap-2 border-b border-gray-100 py-2 pl-3 pr-3",
                disabled ? "cursor-default opacity-60" : "cursor-pointer",
                selected ? "bg-primary-50" : !disabled && "hover:bg-gray-50",
              )}
              onClick={() => !disabled && onSelect(c.id)}
            >
              {(selected || overdue) && (
                <span
                  className={classNames(
                    "absolute inset-y-0 left-0 w-[3px]",
                    selected ? "bg-primary-600" : "bg-red-600",
                  )}
                />
              )}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <span
                    className={classNames(
                      "h-1.5 w-1.5 shrink-0 rounded-full",
                      c.unread ? "bg-primary-600" : "bg-transparent",
                    )}
                  />
                  <ChannelBadge channel={c.channel} short />
                  <span
                    className={classNames(
                      "min-w-0 truncate text-[13px]",
                      c.unread
                        ? "font-semibold text-gray-900"
                        : "text-gray-800",
                    )}
                  >
                    {c.buyer.username}
                  </span>
                  <span className="ml-auto">
                    <ReplyByChip text={countdown.text} tone={countdown.tone} />
                  </span>
                </div>
                <div className="mt-1 flex items-center gap-1.5 pl-3">
                  <span className="min-w-0 flex-1 truncate text-[12px] text-gray-500">
                    {c.summary}
                  </span>
                  {c.hasNotes && (
                    <span title={t("Has a note")} className="shrink-0 text-violet-500">
                      <DocumentTextIcon className="h-3.5 w-3.5" />
                    </span>
                  )}
                  {c.status === "needs_reply" && (c.waitingMessages ?? 0) > 1 && (
                    <span title={t("The buyer has written again while waiting")} className="shrink-0 rounded-full bg-gray-100 px-1.5 text-[11px] font-medium text-gray-600">
                      {t("{{count}} msgs", { count: c.waitingMessages })}
                    </span>
                  )}
                  {chip && <Chip tone={chip.tone}>{chip.text}</Chip>}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
